import type { ApolloError, ApolloQueryResult } from '@apollo/client';
import { useEffect } from 'react';
import {
  type GetMyAccountsQuery,
  type Exact,
  useGetMyAccountsQuery,
} from '~/graphql/types';
import hasValue from '~/util/hasValue';
import {
  SELECTED_ACCOUNT_STORAGE_KEY,
  ACCOUNT_ID_CURRENTLY_IMPERSONATED,
  removeLocalStorageItem,
} from '~/util/localStorageKeys';

type ReturnType = {
  data?: GetMyAccountsQuery;
  loading?: boolean;
  error?: ApolloError;
  refetch: (
    variables?:
      | Partial<
          Exact<{
            [key: string]: never;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<GetMyAccountsQuery>>;
  accountId: string | null;
} | void;

const IMPERSONATION_VALIDATION_DELAY = 1000;

/**
 * Custom hook to retrieve and manage the current account from the list of available accounts.
 *
 * This hook fetches account data using `useGetMyAccountsQuery`, manages local storage to determine
 * the currently selected or impersonated account, and handles edge cases such as expired
 * impersonation sessions.
 *
 * @returns An object containing:
 *   - `data`: Query result data containing the list of user accounts.
 *   - `loading`: A boolean indicating if the query is in progress.
 *   - `error`: An ApolloError instance if the query fails.
 *   - `refetch`: A function to manually refetch the query data.
 *   - `accountId`: The ID of the currently active account, determined by the following priority:
 *     1. Valid impersonated account (if present in localStorage and found in account list).
 *     2. User-selected account (if stored in localStorage and found in account list).
 *     3. First account in the list (default, also saved to localStorage).
 *     4. `null` (if no accounts are available or query is loading).
 *
 * Account Selection Priority:
 * 1. **Impersonation**: If a valid impersonated account exists, it takes precedence.
 * 2. **User Selection**: If the user has previously selected an account, use that.
 * 3. **Default**: Use the first account in the list and persist it to localStorage.
 * 4. **Empty State**: Return `null` if no accounts are available.
 *
 * Impersonation Cleanup:
 * - When an impersonated account ID exists in localStorage but is not found in the fetched accounts,
 *   the hook validates this state with a delayed refetch to handle race conditions during
 *   impersonation start. If the account remains invalid after validation, it removes the
 *   impersonation from localStorage and reloads the page.
 */
const useGetCurrentAccount = (): ReturnType => {
  const { data, loading, error, refetch } = useGetMyAccountsQuery();

  /**
   * Validates and cleans up invalid impersonation sessions when account data changes.
   *
   * This effect runs whenever `data` changes to check if the currently impersonated account
   * is still valid. If an invalid impersonation is detected, it waits for a delay (to handle
   * race conditions) before refetching and cleaning up if necessary.
   */
  useEffect(() => {
    if (!data || data.getMyAccounts.length === 0) {
      return;
    }

    // Recalculate these values whenever account data changes
    const accounts = data.getMyAccounts;
    const currentlyImpersonatedAccountId = global.localStorage.getItem(
      ACCOUNT_ID_CURRENTLY_IMPERSONATED,
    );
    const validImpersonationAccount = accounts.find(
      ({ id }) => id === currentlyImpersonatedAccountId,
    );

    const needsImpersonationCleanup =
      hasValue(currentlyImpersonatedAccountId) && !validImpersonationAccount;

    /**
     * Handles cleanup of invalid impersonation sessions:
     *
     * Expired Impersonation Session:
     * If a user keeps the window open and their impersonation session expires, the account
     * will no longer appear in the query results. In this case, we validate the state with
     * a delayed refetch to ensure the account is truly invalid (not just a temporary query
     * state), then clean up localStorage and reload the page to reset the application state.
     *
     * Solution:
     * - When an invalid impersonation is detected, wait 1 second and refetch the data.
     * - If the account is still invalid after refetch, clean up and reload.
     * - During validation, the hook continues to return the impersonation ID to prevent UI flicker.
     */
    if (needsImpersonationCleanup) {
      const timeoutId = setTimeout(async () => {
        const result = await refetch();
        const freshAccounts = result.data.getMyAccounts;
        const stillInvalid = !freshAccounts.find(
          acc => acc.id === currentlyImpersonatedAccountId,
        );

        if (stillInvalid) {
          removeLocalStorageItem(ACCOUNT_ID_CURRENTLY_IMPERSONATED);
          window.location.reload();
        }
      }, IMPERSONATION_VALIDATION_DELAY);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return;
  }, [data, refetch]);

  const queryProps = { data, loading, error, refetch };

  // Return early if query is still loading
  if (loading) return { data, loading, error, refetch, accountId: null };

  // Return early if no accounts are available
  if (!data || data.getMyAccounts.length === 0)
    return { ...queryProps, accountId: null };

  const accounts = data.getMyAccounts;
  const selectedAccountId = global.localStorage.getItem(
    SELECTED_ACCOUNT_STORAGE_KEY,
  );
  const currentlyImpersonatedAccountId = global.localStorage.getItem(
    ACCOUNT_ID_CURRENTLY_IMPERSONATED,
  );
  const validImpersonationAccount = accounts.find(
    acc => acc.id === currentlyImpersonatedAccountId,
  );

  // If impersonation account is invalid, return it anyway to prevent flicker while validation runs
  // The useEffect above will handle cleanup and page reload if validation confirms it's invalid
  if (hasValue(currentlyImpersonatedAccountId) && !validImpersonationAccount) {
    return { ...queryProps, accountId: currentlyImpersonatedAccountId };
  }

  // Priority 1: Valid impersonated account
  if (validImpersonationAccount)
    return { ...queryProps, accountId: currentlyImpersonatedAccountId };

  // Priority 2: User-selected account (if valid)
  if (selectedAccountId && accounts.find(acc => acc.id === selectedAccountId))
    return { ...queryProps, accountId: selectedAccountId };

  // Priority 3: Default to first account and persist selection
  const headAccountId = accounts[0].id;
  global.localStorage.setItem(SELECTED_ACCOUNT_STORAGE_KEY, headAccountId);
  return { ...queryProps, accountId: headAccountId };
};

export default useGetCurrentAccount;
